import { useState } from 'react';
import { saveAs } from 'file-saver';

import { useMsal } from "@azure/msal-react";

import { TicketAttachment, TicketAttachmentFile } from '../../../../../../../../api-client';
import { IHookCallback } from '../../../../../../../../common/models';
import { TicketPublicApiClient } from '../../../../../../../../providers/api-provider';
import { NotificationHelper } from '../../../../../../../../utils/notification';
import { useTranslation } from 'react-i18next';
import helpers from '../../../../../../../../utils/helpers/helpers';

export const useViewAttachment = () => {
    const {
        instance
    } = useMsal();

    const {
        t: translate
    } = useTranslation();

    interface IRetrieveTicketAttachments extends IHookCallback {
        id: string;
    }

    const [ticketAttachments, setTicketAttachments] = useState<TicketAttachment[]>();
    const [ticketAttachmentsLoading, setTicketAttachmentsLoading] = useState<boolean>(false);

    const handleRetrieveTicketAttachments = async (params: IRetrieveTicketAttachments) => {
        setTicketAttachmentsLoading(true);

        const clientApi = await TicketPublicApiClient(instance, helpers.getStoredI18Lang());

        clientApi.getTicketAttachments(params.id)
            .then((response) => {
                setTicketAttachments(response.data);

                params.onSuccess && params.onSuccess();
            })
            .catch((reason) => {
                setTicketAttachments([]);

                params.onError && params.onError();

                NotificationHelper.createNotification({
                    type: 'error',
                    message: translate('system.notification.getViewAttachments.title'),
                    title: translate('system.notification.getViewAttachments.message')
                });
            })
            .finally(() => {
                setTicketAttachmentsLoading(false);

                params.onAlways && params.onAlways();
            });
    }

    interface IRetrieveTicketAttachment extends IHookCallback {
        id: string;
        attachmentId: string;
        fileName: string;
    }

    const [ticketAttachmentLoading, setTicketAttachmentLoading] = useState<boolean>(false);

    const handleRetrieveTicketAttachment = async (params: IRetrieveTicketAttachment) => {
        setTicketAttachmentLoading(true);

        const clientApi = await TicketPublicApiClient(instance, helpers.getStoredI18Lang());

        clientApi.getTicketAttachment(params.id, params.attachmentId, { responseType: 'blob' })
            .then((response) => {
                saveAs(new Blob([response.data]), `${params.fileName}`);

                params.onSuccess && params.onSuccess();
            })
            .catch((reason) => {
                params.onError && params.onError();

                NotificationHelper.createNotification({
                    type: 'error',
                    message: translate('system.notification.getViewAttachment.title'),
                    title: translate('system.notification.getViewAttachment.message')
                });
            })
            .finally(() => {
                setTicketAttachmentLoading(false);

                params.onAlways && params.onAlways();
            });
    }

    // Upload Attachments

    interface IUploadTicketAttachment extends IHookCallback {
        id: string;
        attachment: TicketAttachmentFile;
    }

    const [uploadTicketAttachmentLoading, setUploadTicketAttachmentLoading] = useState<boolean>(false);

    const handleUploadTicketAttachment = async (params: IUploadTicketAttachment) => {
        setUploadTicketAttachmentLoading(true);

        const clientApi = await TicketPublicApiClient(instance, helpers.getStoredI18Lang());

        clientApi.insertTicketAttachment(params.id, params.attachment)
            .then((response) => {
                params.onSuccess && params.onSuccess();

                NotificationHelper.createNotification({
                    type: 'success',
                    message: translate('system.notification.insertViewAttachmentSuccess.title'),
                    title: translate('system.notification.insertViewAttachmentSuccess.message')
                });
            })
            .catch((reason) => {
                params.onError && params.onError();

                NotificationHelper.createNotification({
                    type: 'error',
                    message: translate('system.notification.insertViewAttachment.title'),
                    title: translate('system.notification.insertViewAttachment.message')
                });
            })
            .finally(() => {
                setUploadTicketAttachmentLoading(false);

                params.onAlways && params.onAlways();
            });
    }

    return {
        handleRetrieveTicketAttachments,
        ticketAttachments,
        ticketAttachmentsLoading,
        handleRetrieveTicketAttachment,
        ticketAttachmentLoading,
        handleUploadTicketAttachment,
        uploadTicketAttachmentLoading
    }
}